import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  MagnifyingGlassIcon,
  ChatBubbleLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface Message {
  id: string;
  sender: {
    name: string;
    email: string;
    avatar: string;
  };
  recipient: {
    name: string;
    email: string;
  };
  subject: string;
  preview: string;
  sentAt: Date;
  status: 'delivered' | 'failed' | 'flagged';
  reportCount: number;
}

const AdminMessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadMessages();
  }, [filterStatus]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API call
      const mockMessages: Message[] = [
        {
          id: '1',
          sender: {
            name: 'John Doe',
            email: 'john@example.com',
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=John',
          },
          recipient: {
            name: 'Jane Smith',
            email: 'jane@example.com',
          },
          subject: 'Thank you for your support!',
          preview: 'I really appreciate your continued support...',
          sentAt: new Date('2024-01-15T10:30:00'),
          status: 'delivered',
          reportCount: 0,
        },
        {
          id: '2',
          sender: {
            name: 'Alice Johnson',
            email: 'alice@example.com',
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alice',
          },
          recipient: {
            name: 'Bob Wilson',
            email: 'bob@example.com',
          },
          subject: 'Exclusive content for you',
          preview: 'Check out this exclusive content...',
          sentAt: new Date('2024-01-15T09:15:00'),
          status: 'flagged',
          reportCount: 3,
        },
        {
          id: '3',
          sender: {
            name: 'Mike Brown',
            email: 'mike@example.com',
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mike',
          },
          recipient: {
            name: 'Sarah Davis',
            email: 'sarah@example.com',
          },
          subject: 'Update on my latest project',
          preview: 'Failed to deliver message...',
          sentAt: new Date('2024-01-15T08:00:00'),
          status: 'failed',
          reportCount: 0,
        },
      ];
      setMessages(mockMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMessages = messages.filter((msg) => {
    const matchesSearch =
      msg.sender.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.sender.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.recipient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || msg.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: messages.length,
    delivered: messages.filter((m) => m.status === 'delivered').length,
    failed: messages.filter((m) => m.status === 'failed').length,
    flagged: messages.filter((m) => m.status === 'flagged').length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Platform Messages</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Messages</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <ChatBubbleLeftIcon className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Delivered</p>
                <p className="text-2xl font-bold text-gray-900">{stats.delivered}</p>
              </div>
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-gray-900">{stats.failed}</p>
              </div>
              <XCircleIcon className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Flagged</p>
                <p className="text-2xl font-bold text-gray-900">{stats.flagged}</p>
              </div>
              <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by sender, recipient, or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="flagged">Flagged</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMessages.map((msg) => (
                <Card key={msg.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <img
                        src={msg.sender.avatar}
                        alt={msg.sender.name}
                        className="w-12 h-12 rounded-full"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900">{msg.subject}</p>
                            <p className="text-sm text-gray-600">
                              From: {msg.sender.name} ({msg.sender.email})
                            </p>
                            <p className="text-sm text-gray-600">
                              To: {msg.recipient.name} ({msg.recipient.email})
                            </p>
                            <p className="text-sm text-gray-500 mt-2">{msg.preview}</p>
                            <p className="text-xs text-gray-400 mt-2">
                              {msg.sentAt.toLocaleString()}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                msg.status === 'delivered'
                                  ? 'bg-green-100 text-green-800'
                                  : msg.status === 'failed'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {msg.status.charAt(0).toUpperCase() + msg.status.slice(1)}
                            </span>
                            {msg.reportCount > 0 && (
                              <span className="flex items-center gap-1 text-red-600 text-sm">
                                <ExclamationTriangleIcon className="w-4 h-4" />
                                {msg.reportCount} reports
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 flex gap-2">
                          <Button size="sm">View Details</Button>
                          {msg.status === 'flagged' && (
                            <>
                              <Button variant="secondary" size="sm">
                                Approve
                              </Button>
                              <Button variant="danger" size="sm">
                                Delete
                              </Button>
                            </>
                          )}
                          {msg.status === 'failed' && (
                            <Button variant="secondary" size="sm">
                              Retry Send
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMessagesPage;
